import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 30px;
  color: var(--text-color);
  text-align: center;
`;

const ProductDescription = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 40px;
  color: var(--text-color);
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ProductFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? 'var(--accent-color)' : 'transparent'};
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--text-color)'};
  border: 1px solid ${props => props.active ? 'var(--accent-color)' : 'var(--border-color)'};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#c89a3e' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const ProductCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ProductImage = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: 20px;
  background-color: var(--card-background);
`;

const ProductTitle = styled.h3`
  margin-bottom: 8px;
  font-size: 1.3rem;
  color: var(--text-color);
`;

const ProductPrice = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--accent-color);
  margin-bottom: 15px;
`;

const ProductDetails = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const DetailsButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--primary-color);
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c89a3e;
  }
`;

const CreateButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--primary-color);
  padding: 10px 24px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c89a3e;
    transform: translateY(-2px);
  }
`;

const CenteredContainer = styled.div`
  text-align: center;
  margin-top: 30px;
`;

const ProductsPage = () => {
    // Sample products data
    const products = [
        {
            id: 1,
            title: "Classic Star Map",
            price: 49.99,
            details: "11\"×14\" print on premium matte paper",
            imageUrl: "/images/products/classic-map.jpg",
            category: "prints"
        },
        {
            id: 2,
            title: "Minimalist Star Map",
            price: 49.99,
            details: "11\"×14\" print on premium matte paper",
            imageUrl: "/images/products/minimalist-map.jpg",
            category: "prints"
        },
        {
            id: 3,
            title: "Colorful Star Map",
            price: 59.99,
            details: "11\"×14\" print on premium matte paper",
            imageUrl: "/images/products/colorful-map.jpg",
            category: "prints"
        },
        {
            id: 4,
            title: "Framed Classic Map",
            price: 89.99,
            details: "11\"×14\" print with wooden frame",
            imageUrl: "/images/products/framed-classic.jpg",
            category: "framed"
        },
        {
            id: 5,
            title: "Framed Minimalist Map",
            price: 89.99,
            details: "11\"×14\" print with wooden frame",
            imageUrl: "/images/products/framed-minimalist.jpg",
            category: "framed"
        },
        {
            id: 6,
            title: "Canvas Star Map",
            price: 99.99,
            details: "16\"×20\" gallery-wrapped canvas",
            imageUrl: "/images/products/canvas-map.jpg",
            category: "canvas"
        },
        {
            id: 7,
            title: "Metal Print Star Map",
            price: 119.99,
            details: "11\"×14\" aluminum metal print",
            imageUrl: "/images/products/metal-map.jpg",
            category: "metal"
        },
        {
            id: 8,
            title: "Digital Star Map",
            price: 29.99,
            details: "High-resolution digital download",
            imageUrl: "/images/products/digital-map.jpg",
            category: "digital"
        }
    ];

    // Filter state
    const [activeFilter, setActiveFilter] = useState('all');

    // Filter categories
    const categories = [
        { id: 'all', name: 'All Products' },
        { id: 'prints', name: 'Paper Prints' },
        { id: 'framed', name: 'Framed Maps' },
        { id: 'canvas', name: 'Canvas Prints' },
        { id: 'metal', name: 'Metal Prints' },
        { id: 'digital', name: 'Digital Downloads' }
    ];

    // Filter products based on active filter
    const filteredProducts = activeFilter === 'all'
        ? products
        : products.filter(product => product.category === activeFilter);

    return (
        <PageContainer>
            <Container>
                <SectionTitle>Our Products</SectionTitle>

                <ProductDescription>
                    We offer a variety of premium products featuring your personalized star map.
                    Each is crafted with care using high-quality materials to preserve your special moment.
                </ProductDescription>

                <ProductFilters>
                    {categories.map(category => (
                        <FilterButton
                            key={category.id}
                            active={activeFilter === category.id}
                            onClick={() => setActiveFilter(category.id)}
                        >
                            {category.name}
                        </FilterButton>
                    ))}
                </ProductFilters>

                <ProductGrid>
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id}>
                            <ProductImage>
                                <img src={product.imageUrl} alt={product.title} />
                            </ProductImage>
                            <ProductInfo>
                                <ProductTitle>{product.title}</ProductTitle>
                                <ProductPrice>${product.price.toFixed(2)}</ProductPrice>
                                <ProductDetails>{product.details}</ProductDetails>
                                <DetailsButton to="/create">Customize</DetailsButton>
                            </ProductInfo>
                        </ProductCard>
                    ))}
                </ProductGrid>

                <CenteredContainer>
                    <CreateButton to="/create">Create Your Custom Star Map</CreateButton>
                </CenteredContainer>
            </Container>
        </PageContainer>
    );
};

export default ProductsPage; 